import { createStyles, makeStyles, Theme } from '@material-ui/core';
import React, { useMemo } from 'react';

import {
  CLIENT_APPS_PAGE,
  COMPANIES_PAGE,
  CONTRACT_DETAILS_PAGE,
  CONTRACTS_PAGE,
  FILES_PAGE,
  FORMS_DETAIL_PAGE_V2,
  FORMS_PAGE,
  PAYMENTS_INVOICES_PAGE,
  USERS_PAGE,
} from 'src/constants';
import { InboxNotificationDetailsResponse } from 'src/entities/Notifications';
import {
  ContractDetailsPageView,
  ContractPageTabs,
} from 'src/legacy/components/Contracts';
import { useGetContractsQuery } from 'src/services/api';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { useGetFormByIdQuery } from 'src/services/api/formsApi';

import { InboxSidebarItemData } from './InboxSidebar';
import { NotificationContentBreadcrumbs } from './NotificationContentBreadcrumbs';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: (props: { isFileNotification: boolean }) =>
        props.isFileNotification
          ? theme.spacing(2.5, 0, 1, 2.5)
          : theme.spacing(0),
    },
  }),
);

type Props = {
  selectedNotification: InboxSidebarItemData;
  selectedNotificationDetails: InboxNotificationDetailsResponse | undefined;
  children: React.ReactNode; // This is the content of the notification details (PDFViewer, FilesTable, FormResponse, Invoice)
};
export const NotificationDetailsContainer = ({
  selectedNotification,
  selectedNotificationDetails,
  children,
}: Props) => {
  const {
    resourceId,
    notificationEventType,
    channelId,
    avatarInfo,
    refId: responseRefId,
  } = selectedNotification || {};

  const contractTemplateId = selectedNotification?.channelId;
  const isOneOffContract = !contractTemplateId;
  const { data: contracts } = useGetContractsQuery({
    contractTemplateId,
  });
  const { data: formTemplate } = useGetFormByIdQuery(resourceId as string, {
    skip: !resourceId || notificationEventType !== 'formResponse.completed',
  });
  const contract = contracts?.find((c) => c.id === resourceId);
  const invoiceNumber =
    selectedNotificationDetails?.additionalFields?.invoiceNumber ||
    selectedNotification?.resourceId;

  const invoiceId = selectedNotification?.refId;

  const classes = useStyles({
    isFileNotification:
      selectedNotification?.notificationEventType === 'files.new' ||
      selectedNotification?.notificationEventType === 'files.created',
  });

  const { data: { extensionsSettings } = {} } = useGetInstallsQuery();

  const items = useMemo(() => {
    switch (notificationEventType) {
      case 'formResponse.completed':
      case 'forms.completed':
        return [
          { label: 'Forms', path: `${FORMS_PAGE.path}` },
          {
            label: formTemplate?.fields.name || '',
            path: `${FORMS_DETAIL_PAGE_V2.path}?formId=${resourceId}&view=see_responses`,
          },
          {
            label: avatarInfo?.name || '',
            path: `${FORMS_PAGE.path}/submit?formId=${resourceId}&responseRefId=${responseRefId}`,
          },
        ];
      case 'invoice.paid':
      case 'invoice.processing':
      case 'invoice.paymentFailed':
        return [
          {
            label: 'Invoices',
            path: `${PAYMENTS_INVOICES_PAGE.path}`,
          },
          {
            label: invoiceNumber,
            path: `${PAYMENTS_INVOICES_PAGE.path}/details?invoiceId=${invoiceId}`,
          },
        ];
      case 'files.new':
      case 'files.created':
        return [
          { label: 'Files', path: `${FILES_PAGE.path}` },
          {
            label: avatarInfo?.name || '',
            path: `${FILES_PAGE.path}?channelId=${channelId}`,
          },
        ];
      case 'esign.completed':
      case 'contract.signed':
        return [
          { label: 'Contracts', path: `${CONTRACTS_PAGE.path}` },
          {
            label: contract?.name || '',
            path: isOneOffContract
              ? `${CONTRACTS_PAGE.path}?view=${ContractPageTabs.Submissions}`
              : `${CONTRACT_DETAILS_PAGE.path}?templateId=${contractTemplateId}&view=${ContractDetailsPageView.SubmissionRequests}`,
          },
          {
            label: avatarInfo?.name || '',
            path: `${CONTRACTS_PAGE.path}/submission?contractId=${resourceId}`,
          },
        ];
      case 'internalChat.mentioned':
        return [
          {
            label:
              selectedNotification.channelRecipientType === 'client'
                ? 'Clients'
                : 'Companies',
            path:
              selectedNotification.channelRecipientType === 'client'
                ? `${USERS_PAGE.path}`
                : `${COMPANIES_PAGE.path}`,
          },
          {
            label:
              selectedNotificationDetails?.additionalFields?.channelName || '',
            path:
              selectedNotification.channelRecipientType === 'client'
                ? `${USERS_PAGE.path}/details?clientUserId=${selectedNotification.channelRecipientId}`
                : `${COMPANIES_PAGE.path}/${selectedNotification.channelRecipientId}`,
          },
        ];
      case 'customApp.action':
        if (selectedNotificationDetails && extensionsSettings) {
          const appName =
            extensionsSettings[selectedNotificationDetails?.resourceId]?.name ??
            '';
          const queryParams = new URLSearchParams({
            id: selectedNotificationDetails?.resourceId,
          });
          const ctaQueryParams = new URLSearchParams({
            id: selectedNotificationDetails?.resourceId,
            ...(selectedNotificationDetails?.deliveryTargets?.inProduct
              ?.ctaParams ?? {}),
          });
          return [
            {
              label: appName,
              path: `${CLIENT_APPS_PAGE.path}?${queryParams.toString()}`,
            },
            {
              label: avatarInfo?.name || '',
              path: `${CLIENT_APPS_PAGE.path}?${ctaQueryParams.toString()}`,
            },
          ];
        }

        return [];

      default:
        return [];
    }
  }, [
    channelId,
    contract?.name,
    formTemplate?.fields.name,
    contractTemplateId,
    notificationEventType,
    resourceId,
    avatarInfo,
    extensionsSettings,
  ]);

  return (
    <>
      <div className={classes.root}>
        <NotificationContentBreadcrumbs items={items} />
      </div>
      {children}
    </>
  );
};
